import { useCallback, useEffect } from 'react';

import { ARKOSE_TOKEN } from 'consts/storageConsts';

import {
  trackArkoseChallengeHidden,
  trackArkoseChallengeShown,
  trackArkoseEnforcementComplete,
  trackArkoseEnforcementRun,
  trackArkoseJSError,
  trackArkoseJSTimeout,
  trackArkoseLoaded,
  trackArkoseReset,
  trackArkoseSDKError,
} from 'utils/segment/trackingEvents';
import {
  deleteValueFromStorage,
  getValueFromStorage,
  setValueInStorage,
} from 'utils/storage/storageHelper';

window.Arkose = {};

function setupEnforcement(enforcementObject) {
  window.Arkose = enforcementObject;
  window.arkoseLoaded = false;

  setTimeout(() => {
    if (!window.arkoseLoaded) {
      setValueInStorage(ARKOSE_TOKEN, 'ARKOSE_JS_TIMEOUT');
      trackArkoseJSTimeout();
    }
  }, 5000);

  window.Arkose.setConfig({
    onReady: function () {
      window.arkoseLoaded = true;
      window.arkoseComplete = false;
      window.firedArkoseCompleteTrigger = false;
      trackArkoseLoaded();
    },
    // onShow: () => console.error({ eventType: 'onShow' }),
    onShown: function () {
      trackArkoseChallengeShown();
      window.arkoseChallengeShown = true;
    },
    // onSuppress: () => console.error({ eventType: 'onSuppress' }),
    onError: function (response) {
      const arkoseError = response.error;
      setValueInStorage(ARKOSE_TOKEN, 'ARKOSE_SDK_ERROR');
      trackArkoseSDKError(arkoseError.error);
    },
    // onFailed: () => console.error({ eventType: 'onFailed' }),
    onHide: function () {
      /** If they saw the challenge and did not complete it
       * fire a Segment event and re-show the challenge until they complete
       * it. */
      if (window.arkoseChallengeShown && !window.arkoseComplete) {
        // // flag this so we don't submit to server
        window.arkoseComplete = false;
        trackArkoseChallengeHidden();

        trackArkoseEnforcementRun();
        window.Arkose.run();
      }
    },
    onCompleted: function (response) {
      window.arkoseComplete = true;

      // token passed to server in submit, then cleared
      setValueInStorage(ARKOSE_TOKEN, response.token);
      trackArkoseEnforcementComplete();

      // this addl flag is only needed until Arkose helps fix this bug
      // https://support.arkoselabs.com/hc/en-us/requests/3088
      if (!window.firedArkoseCompleteTrigger) {
        // triggers page submit
        const event = new Event('arkoseCompleted');
        window.dispatchEvent(event);
        window.firedArkoseCompleteTrigger = true;
      }
    },
  });
}

window.setupEnforcement = setupEnforcement;

export function createArkoseScript() {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = `${window.Chime.config.ARKOSE_CLIENT_URL}/v2/${window.Chime.config.ARKOSE_PUBLIC_KEY}/api.js`;
  script.setAttribute('data-callback', 'setupEnforcement');
  script.async = true;
  script.defer = true;
  script.id = 'arkosescript';
  script.onerror = function () {
    setValueInStorage(ARKOSE_TOKEN, 'ARKOSE_JS_ERROR');
    trackArkoseJSError();
  };

  const headElement = document.getElementsByTagName('head')[0];

  headElement.appendChild(script);
}

export function resetArkose() {
  const needsTracking = window.arkoseComplete;

  window.arkoseComplete = false;
  window.arkoseChallengeShown = false;
  window.firedArkoseCompleteTrigger = false;

  if (needsTracking) {
    trackArkoseReset();
  }
}

export function useArkose(submitFunction) {
  const submitUponArkoseComplete = useCallback(() => {
    submitFunction();

    window.removeEventListener('arkoseCompleted', submitUponArkoseComplete);
  }, [submitFunction]);

  useEffect(() => {
    if (submitFunction) {
      window.addEventListener('arkoseCompleted', submitUponArkoseComplete);
    }

    return () => {
      window.removeEventListener('arkoseCompleted', submitUponArkoseComplete);
    };
  }, [submitFunction, submitUponArkoseComplete]);

  return {};
}

export function addArkoseToPayload(submissionData) {
  submissionData.arkose_token = getValueFromStorage(ARKOSE_TOKEN);
  deleteValueFromStorage(ARKOSE_TOKEN);

  return submissionData;
}
